/**
 * Onboarding's decision tree: where each page sends the visitor next.
 *
 * Pages report what happened ("the household was found", "CONTINUE was clicked") as an
 * OnboardingEvent; nextOnboardingStep() decides the destination from that event and the flow's
 * context.  Pages keep their side effects (server calls, local storage) and navigate with
 * goToNextOnboardingStep() in common.tsx, which gathers the context from the URL, local storage and
 * platform.
 *
 * This module is pure - no React, no window - so the whole tree is testable as a table
 * (onboarding_flow.test.ts); the flow tests in test/onboarding_flows.test.tsx drive it end to end.
 */
import { Household, HouseholdStatus } from '../../../../common/src/model.ts';
import { OnboardingPages, Pages } from '../../../../common/src/pages.ts';
import { ensureUnreachable } from '../../../../common/src/util.ts';

// The query parameters the flow adds as it goes; each carries context to later pages
export enum OnboardingParam {
  Subscribe = 'subscribe', // the visitor is on their way to subscribing
  // a promotional campaign slug (GitHub issue #3551); the server builds landing URLs with it and
  // `subscribe`, so both must match common/src/pages.ts (pinned by onboarding_flow.test.ts)
  Promo = 'promo',
  EditAll = 'edit-all', // ask for every household field, even ones already known
  FromHouseholdJoin = 'from-household-join', // choose user was reached by joining an existing household
  FromChooseUser = 'from-choose-user', // an invite page was reached after choosing the user
}

export enum OnboardingPlatform {
  DesktopBrowser = 'desktopBrowser',
  MobileBrowser = 'mobileBrowser',
  IOSApp = 'iosApp',
  AndroidApp = 'androidApp',
}

export interface OnboardingContext {
  // from the query parameters
  subscribing: boolean; // `subscribe`
  adminInvite: boolean; // `org-admin-token` (or the retired `church-admin-token`)
  householdInvite: boolean; // `org-id` (or the retired `church-id`) without an admin token
  editAll: boolean; // `edit-all`
  fromHouseholdJoin: boolean; // `from-household-join`
  // the household in local storage, if any
  household: Household | undefined;
  platform: OnboardingPlatform;
  // a promotional campaign on the URL that can be redeemed here: `promo`, in a browser only, since
  // the store apps subscribe through Apple or Google billing (GitHub issue #3551)
  promo: boolean;
}

// Where every onboarding sequence drops the household when it finishes: the Church tab, where
// their church's music is (GitHub issue #3551). "/" would open the most recently used tab instead.
export const OnboardingLandingPage = Pages.Organization; // the Church tab

export type InvitePage = Pages.AdminInvite | Pages.HouseholdInvite;

export type OnboardingDestination =
  // another onboarding page: the current query parameters carry over, plus `addParams`
  | { kind: 'onboarding'; page: OnboardingPages; addParams?: OnboardingParam[] }
  // back to the invite page the flow started from, which reads the invite's query parameters, so
  // they carry over too
  | { kind: 'invite'; page: InvitePage; addParams?: OnboardingParam[] }
  // out of onboarding to an app page, dropping the query parameters
  | { kind: 'app'; page: Pages; replace?: boolean }
  // out of onboarding to "/", which lands on the most recent footer tab
  | { kind: 'home' }
  // a promotional campaign's household is known: straight to Stripe Checkout with the promotion,
  // falling back to the subscribe page if checkout cannot start (GitHub issue #3551)
  | { kind: 'promoCheckout' }
  // create the household now from the onboarding last name and email, which were confirmed earlier
  // (the page that can create does so; anywhere else this means the create confirmation page)
  | { kind: 'createHousehold' };

export type SubscribePage = OnboardingPages.SubscribeStart | OnboardingPages.SubscribePayment;

export type OnboardingEvent =
  | { from: OnboardingPages.Demo; action: 'getStarted' | 'alreadyHaveHousehold' | 'householdExists' }
  | { from: OnboardingPages.SurveyDiscovery; action: 'originChosen' }
  | { from: OnboardingPages.SurveyHouseholdSchedule; action: 'continue' }
  | { from: OnboardingPages.AllowNotifications; action: 'continue' }
  | { from: OnboardingPages.HouseholdStart; action: 'continue' }
  | { from: OnboardingPages.HouseholdLastName; action: 'continue'; householdEmailKnown: boolean }
  | { from: OnboardingPages.HouseholdEmail; action: 'lastNameMissing' | 'householdFound' | 'householdNotFound' }
  | {
      from: OnboardingPages.HouseholdCreate;
      action: 'missingInformation' | 'createFailed' | 'created' | 'editInformation';
    }
  | { from: OnboardingPages.HouseholdFirstName; action: 'continue' }
  | { from: OnboardingPages.HouseholdFind; action: 'notFound' }
  | { from: OnboardingPages.HouseholdFind; action: 'found'; household: Household }
  | { from: OnboardingPages.HouseholdNotFound; action: 'tryAgain' | 'newHousehold' }
  | { from: OnboardingPages.HouseholdJoin; action: 'join'; household: Household }
  | { from: OnboardingPages.HouseholdJoin; action: 'householdMissing' | 'editEmail' }
  | { from: OnboardingPages.HouseholdWelcome; action: 'continue' }
  | { from: OnboardingPages.OrgChoice; action: 'continue' | 'orgNotListed' }
  | { from: OnboardingPages.ShareMusicLeader; action: 'continue' }
  // choose user and the edit user panel inside it, once the visitor is chosen or added
  | { from: OnboardingPages.ChooseUser; action: 'userChosen' }
  | { from: OnboardingPages.SubscribeFindHousehold; action: 'alreadyHaveHousehold' | 'newHousehold' }
  | { from: SubscribePage; action: 'noHousehold' | 'alreadySubscribed' | 'purchasedInApp' }
  // a subscribed household read that the promotion does not apply to it (GitHub issue #3551)
  | { from: SubscribePage; action: 'promoNotApplicable' }
  | { from: OnboardingPages.SubscribeThankYou; action: 'continue'; returnPage: Pages }
  | { from: OnboardingPages.SubscribeThankYou; action: 'contactSupport' | 'startOver' }
  | { from: OnboardingPages.Download; action: 'continue' }
  | { from: Pages.AdminInvite; action: 'noHousehold' | 'chooseUser' | 'continue' }
  | { from: Pages.HouseholdInvite; action: 'noHousehold' | 'continue' };

const onboarding = (page: OnboardingPages, ...addParams: OnboardingParam[]): OnboardingDestination =>
  addParams.length > 0 ? { kind: 'onboarding', page, addParams } : { kind: 'onboarding', page };

const invite = (page: InvitePage, ...addParams: OnboardingParam[]): OnboardingDestination =>
  addParams.length > 0 ? { kind: 'invite', page, addParams } : { kind: 'invite', page };

const home: OnboardingDestination = { kind: 'home' };

const landing: OnboardingDestination = { kind: 'app', page: OnboardingLandingPage };

// The subscribe step for a household that has just been found, joined or set up. A promotional
// campaign's visitor already read the offer before signing in, so an eligible household goes
// straight to checkout rather than seeing the offer twice, and a subscribed one returns to the offer
// page, which explains that the offer does not apply (GitHub issue #3551).
function subscribeStep(household: Household, { promo }: OnboardingContext): OnboardingDestination {
  const subscribed = household.status === HouseholdStatus.Subscribed;
  if (promo) {
    return subscribed ? onboarding(OnboardingPages.SubscribePayment) : { kind: 'promoCheckout' };
  }
  return onboarding(subscribed ? OnboardingPages.SubscribeThankYou : OnboardingPages.SubscribePayment);
}

// where a new or existing household goes once its church is settled
function afterOrgSettled(context: OnboardingContext): OnboardingDestination {
  const { subscribing, household } = context;
  return subscribing && household ? subscribeStep(household, context) : onboarding(OnboardingPages.HouseholdWelcome);
}

// where an invite page goes once the invite is done
function afterInvite({ platform }: OnboardingContext): OnboardingDestination {
  return platform === OnboardingPlatform.MobileBrowser ? onboarding(OnboardingPages.Download) : landing;
}

export function nextOnboardingStep(event: OnboardingEvent, context: OnboardingContext): OnboardingDestination {
  switch (event.from) {
    case OnboardingPages.Demo:
      switch (event.action) {
        case 'getStarted':
          return onboarding(OnboardingPages.SurveyDiscovery);
        case 'alreadyHaveHousehold':
          return onboarding(OnboardingPages.HouseholdFind);
        case 'householdExists':
          return home;
        default:
          return ensureUnreachable(event);
      }

    case OnboardingPages.SurveyDiscovery:
      return onboarding(OnboardingPages.SurveyHouseholdSchedule);

    case OnboardingPages.SurveyHouseholdSchedule:
      if (context.platform === OnboardingPlatform.IOSApp) {
        return onboarding(OnboardingPages.AllowNotifications);
      }
      return context.household
        ? onboarding(OnboardingPages.HouseholdWelcome)
        : onboarding(OnboardingPages.HouseholdStart);

    case OnboardingPages.AllowNotifications:
      return onboarding(OnboardingPages.HouseholdStart);

    case OnboardingPages.HouseholdStart:
      return onboarding(OnboardingPages.HouseholdLastName, OnboardingParam.EditAll);

    case OnboardingPages.HouseholdLastName:
      // a promotional campaign's visitor confirmed their email on household not found, so the household
      // is created now, without asking for the email again or confirming (GitHub issue #3551)
      if (context.promo && event.householdEmailKnown) {
        return { kind: 'createHousehold' };
      }
      return !event.householdEmailKnown || context.editAll
        ? onboarding(OnboardingPages.HouseholdEmail)
        : onboarding(OnboardingPages.HouseholdCreate);

    case OnboardingPages.HouseholdEmail:
      switch (event.action) {
        case 'lastNameMissing':
          return onboarding(OnboardingPages.HouseholdLastName);
        case 'householdFound':
          return onboarding(OnboardingPages.HouseholdJoin);
        case 'householdNotFound':
          return onboarding(OnboardingPages.HouseholdCreate);
        default:
          return ensureUnreachable(event);
      }

    case OnboardingPages.HouseholdCreate:
      switch (event.action) {
        case 'missingInformation':
          return onboarding(OnboardingPages.HouseholdLastName);
        case 'createFailed':
          return onboarding(OnboardingPages.HouseholdEmail);
        case 'created':
          // an admin invite asks who the visitor is itself (choose user); every other new household
          // is asked for its first member's name, so it is never left without a user
          if (context.adminInvite) {
            return invite(Pages.AdminInvite);
          }
          return onboarding(OnboardingPages.HouseholdFirstName);
        case 'editInformation':
          return onboarding(OnboardingPages.HouseholdLastName, OnboardingParam.EditAll);
        default:
          return ensureUnreachable(event);
      }

    case OnboardingPages.HouseholdFirstName:
      // a household invite sets the church itself, so it skips org choice
      return context.householdInvite ? invite(Pages.HouseholdInvite) : onboarding(OnboardingPages.OrgChoice);

    case OnboardingPages.HouseholdFind:
      if (event.action === 'notFound') {
        return onboarding(OnboardingPages.HouseholdNotFound);
      }
      return context.subscribing
        ? subscribeStep(event.household, context)
        : onboarding(OnboardingPages.ChooseUser, OnboardingParam.FromHouseholdJoin);

    case OnboardingPages.HouseholdNotFound:
      switch (event.action) {
        case 'tryAgain':
          return onboarding(OnboardingPages.HouseholdFind);
        case 'newHousehold':
          // an admin invite already knows the org type, so it skips the discovery survey
          return context.adminInvite
            ? onboarding(OnboardingPages.SurveyHouseholdSchedule)
            : onboarding(OnboardingPages.SurveyDiscovery);
        default:
          return ensureUnreachable(event);
      }

    case OnboardingPages.HouseholdJoin:
      switch (event.action) {
        case 'join':
          if (context.adminInvite) {
            return invite(Pages.AdminInvite);
          }
          if (context.householdInvite) {
            return invite(Pages.HouseholdInvite);
          }
          return context.subscribing
            ? subscribeStep(event.household, context)
            : onboarding(OnboardingPages.ChooseUser, OnboardingParam.FromHouseholdJoin);
        case 'householdMissing':
        case 'editEmail':
          return onboarding(OnboardingPages.HouseholdEmail);
        default:
          return ensureUnreachable(event);
      }

    case OnboardingPages.HouseholdWelcome:
      return landing;

    case OnboardingPages.OrgChoice:
      switch (event.action) {
        case 'continue':
          return afterOrgSettled(context);
        case 'orgNotListed':
          return onboarding(OnboardingPages.ShareMusicLeader);
        default:
          return ensureUnreachable(event);
      }

    case OnboardingPages.ShareMusicLeader:
      return afterOrgSettled(context);

    case OnboardingPages.ChooseUser:
      if (context.adminInvite) {
        return invite(Pages.AdminInvite, OnboardingParam.FromChooseUser);
      }
      if (context.householdInvite) {
        return invite(Pages.HouseholdInvite, OnboardingParam.FromChooseUser);
      }
      if (context.fromHouseholdJoin) {
        return onboarding(OnboardingPages.OrgChoice);
      }
      return landing;

    case OnboardingPages.SubscribeFindHousehold:
      switch (event.action) {
        case 'alreadyHaveHousehold':
          return onboarding(OnboardingPages.HouseholdFind, OnboardingParam.Subscribe);
        case 'newHousehold':
          return onboarding(OnboardingPages.SurveyDiscovery, OnboardingParam.Subscribe, OnboardingParam.EditAll);
        default:
          return ensureUnreachable(event);
      }

    case OnboardingPages.SubscribeStart:
    case OnboardingPages.SubscribePayment:
      switch (event.action) {
        case 'noHousehold':
          return onboarding(OnboardingPages.HouseholdFind, OnboardingParam.Subscribe);
        case 'alreadySubscribed':
          return context.platform === OnboardingPlatform.IOSApp || context.platform === OnboardingPlatform.AndroidApp
            ? home
            : onboarding(OnboardingPages.Download);
        case 'purchasedInApp':
          return home;
        case 'promoNotApplicable':
          return landing;
        default:
          return ensureUnreachable(event);
      }

    case OnboardingPages.SubscribeThankYou:
      switch (event.action) {
        case 'continue':
          // a mobile browser is offered the store apps before the app itself, as at the end of every
          // other sequence there (GitHub issue #3551); Download then continues to the Church tab
          return context.platform === OnboardingPlatform.MobileBrowser
            ? onboarding(OnboardingPages.Download)
            : { kind: 'app', page: event.returnPage, replace: true };
        case 'contactSupport':
          return onboarding(OnboardingPages.Download);
        case 'startOver':
          return onboarding(OnboardingPages.SubscribeStart);
        default:
          return ensureUnreachable(event);
      }

    case OnboardingPages.Download:
      return landing;

    case Pages.AdminInvite:
      switch (event.action) {
        case 'noHousehold':
          return onboarding(OnboardingPages.HouseholdFind);
        case 'chooseUser':
          return onboarding(OnboardingPages.ChooseUser);
        case 'continue':
          return afterInvite(context);
        default:
          return ensureUnreachable(event);
      }

    case Pages.HouseholdInvite:
      switch (event.action) {
        case 'noHousehold':
          return onboarding(OnboardingPages.SurveyHouseholdSchedule);
        case 'continue':
          return afterInvite(context);
        default:
          return ensureUnreachable(event);
      }

    default:
      return ensureUnreachable(event);
  }
}
